import FileUpload from './FileUpload';
import React, { useEffect, useState } from 'react';
import { FaCheck, FaDownload, FaEye, FaFileUpload, FaTrash } from 'react-icons/fa';
import { documentDefinition, documentMatchesDefinition, latestDocumentFor } from '../utils/practiceDocuments';
import './PracticeDocumentLinks.css';

export default function PracticeDocumentLinks({ schedaId, suspendedId, title = 'Documenti', documentKeys = [], className = '', onChange }) {
  const archiveUrl = suspendedId ? `/api/documenti/sospesi/${suspendedId}` : `/api/documenti/schede/${schedaId}`;
  const [documents, setDocuments] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const definitions = documentKeys.map(documentDefinition).filter(Boolean);

  const loadDocuments = async () => {
    if ((!schedaId && !suspendedId)) return [];
    const response = await fetch(archiveUrl);
    if (!response.ok) throw new Error('Archivio documenti non disponibile');
    const rows = await response.json();
    setDocuments(rows);
    return rows;
  };

  useEffect(() => {
    let active = true;
    if ((!schedaId && !suspendedId)) return undefined;
    fetch(archiveUrl).then(response => {
      if (!response.ok) throw new Error('Archivio documenti non disponibile');
      return response.json();
    }).then(rows => { if (active) setDocuments(rows); }).catch(cause => { if (active) setError(cause.message); });
    return () => { active = false; };
  }, [schedaId, suspendedId, archiveUrl]);

  const upload = async (event, definition) => {
    const selected = Array.from(event.target.files || []);
    const files = (definition.key === 'accidentPhotos' || definition.multiplePhotos) ? selected : selected.slice(0, 1);
    event.target.value = '';
    if (!files.length || (!schedaId && !suspendedId)) return;
    setBusy(definition.key); setError('');
    try {
      const oversized = files.find(file => file.size > 15 * 1024 * 1024);
      if (oversized) throw new Error(`${oversized.name} supera il limite di 15 MB`);
      for (const file of files) {
      const response = await fetch(archiveUrl, { method: 'POST', headers: {
        'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name),
        'X-File-Type': file.type, 'X-Category': definition.area, 'X-Description': encodeURIComponent(definition.label)
      }, body: file });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(`${file.name}: ${result.error || 'Caricamento non riuscito'}`);
      }
      const rows = await loadDocuments();
      onChange?.(rows);
    } catch (cause) { setError(cause.message); await loadDocuments().then(rows => onChange?.(rows)).catch(() => {}); } finally { setBusy(''); }
  };

  const remove = async (item, definition) => {
    if (!window.confirm(`Eliminare definitivamente “${item.nome_originale || definition.label}”?`)) return;
    setBusy(definition.key); setError('');
    try {
      const response = await fetch(`/api/documenti/${item.id}`, { method: 'DELETE' });
      if (!response.ok) { const result = await response.json().catch(() => ({})); throw new Error(result.error || 'Eliminazione non riuscita'); }
      const rows = await loadDocuments();
      onChange?.(rows);
    } catch (cause) { setError(cause.message); } finally { setBusy(''); }
  };

  if (!definitions.length) return null;
  return <div className={`context-documents ${className}`.trim()}>
    <div className="context-documents-title">{title}</div>
    <div className="context-documents-list">
      {definitions.map(definition => {
        if ((definition.key === 'accidentPhotos' || definition.multiplePhotos)) {
          const photos = documents.filter(item => documentMatchesDefinition(item, definition));
          return <section className="context-document photo-document" key={definition.key}>
            <header><strong>{definition.label}</strong><small>{photos.length} file</small><FileUpload compact multiple showSelectedName={false} label={busy === definition.key ? 'Caricamento…' : '+ Foto'} accept=".jpg,.jpeg,.jfif,.png,.pdf" disabled={(!schedaId && !suspendedId) || Boolean(busy)} onChange={event => upload(event, definition)}/></header>
            {!photos.length ? <small>Puoi selezionare più foto insieme e aggiungerne altre in seguito.</small> : <div className="incident-photo-grid">{photos.map(photo => <article key={photo.id}>
              <a className="incident-photo-preview" href={`/api/documenti/${photo.id}/file?preview=1`} target="_blank" rel="noreferrer" title={`Apri ${photo.nome_originale}`}>
                {['image/jpeg','image/png'].includes(photo.tipo_mime) ? <img src={`/api/documenti/${photo.id}/file?preview=1`} alt={photo.nome_originale} loading="lazy"/> : <FaEye/>}
              </a><small title={photo.nome_originale}>{photo.nome_originale}</small><div><a href={`/api/documenti/${photo.id}/file?preview=1`} target="_blank" rel="noreferrer" title={`Anteprima ${photo.nome_originale}`}><FaEye/></a><a href={`/api/documenti/${photo.id}/file`} title={`Scarica ${photo.nome_originale}`}><FaDownload/></a><button type="button" aria-label={`Elimina ${photo.nome_originale}`} disabled={Boolean(busy)} onClick={() => remove(photo, definition)}><FaTrash/></button></div>
            </article>)}</div>}
          </section>;
        }
        const item = latestDocumentFor(documents, definition);
        const previewable = item && ['application/pdf', 'image/jpeg', 'image/png'].includes(item.tipo_mime);
        return <div className={`context-document ${item ? 'complete' : 'missing'}`} key={definition.key}>
          <span className="context-document-status">{item ? <FaCheck /> : <FaFileUpload />}</span>
          <span className="context-document-name"><strong>{definition.label}</strong>{item && <small title={item.nome_originale}>{item.nome_originale}</small>}</span>
          <span className="context-document-actions">
            {item ? <>
              {previewable && <a href={`/api/documenti/${item.id}/file?preview=1`} target="_blank" rel="noreferrer" title={`Apri ${definition.label}`}><FaEye /></a>}
              <a href={`/api/documenti/${item.id}/file`} title={`Scarica ${definition.label}`}><FaDownload /></a>
              <button type="button" title={`Elimina ${definition.label}`} disabled={Boolean(busy)} onClick={() => remove(item, definition)}><FaTrash /></button>
            </> : <FileUpload compact label={busy === definition.key ? 'Caricamento…' : 'Carica'} accept=".pdf,.doc,.docx,.jpg,.jpeg,.jfif,.png" disabled={(!schedaId && !suspendedId) || Boolean(busy)} onChange={event => upload(event, definition)} />}
          </span>
        </div>;
      })}
    </div>
    {error && <small className="context-documents-error" role="alert">{error}</small>}
  </div>;
}

